/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import type { AlertOptions, BlockVError, WalletError } from '.'

import type { AlertType } from 'react-native'
import { Alert as ReactAlert } from 'react-native'
import type { RootSDKStoreType } from '@vatom/sdk/core'
import { Sentry } from '@vatom/sdk/core'
import { theme } from '@vatom/wombo'
import Swal from 'sweetalert2'

import logger from '../../logger'

import ErrList from './Errors.json'

const ErrorList = ErrList as WalletError[]
class Alert {
  static quickFire(title: string, message?: string) {
    return Swal.fire({ title, text: message })
  }

  static fire(options: AlertOptions) {
    return Swal.fire({
      ...options,
      reverseButtons: true
    })
  }

  static async showError(error: any, sdk?: RootSDKStoreType) {
    if (!error) {
      return
    }

    // Log to console
    logger.warn(error)
    Sentry.captureError(error)
    // Find error details in our table, or use a generic error message
    let detail: WalletError | undefined = ErrorList.find(e => e.code === error.code)

    if (detail?.code === 11) {
      // Try find more info by message
      detail =
        ErrorList.find(e => !!e.server_message && error.message.includes(e.server_message)) ||
        detail
    }

    const customError: any = undefined
    const errorText =
      customError?.text ||
      detail?.client?.message?.body ||
      error.message ||
      `We couldn't perform the requested action.`

    const titleText =
      customError?.title || detail?.client?.message?.title || error.name || 'There was a problem'

    const renderIcon = (type: WalletError['client']['type'] | undefined) => {
      switch (type) {
        case 'error':
          return `<svg width="60" height="60" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10 2.5C10.4375 2.5 10.8438 2.75 11.0625 3.125L17.8125 14.625C18.0312 15.0312 18.0312 15.5 17.8125 15.875C17.5938 16.2812 17.1875 16.5 16.75 16.5H3.25C2.78125 16.5 2.375 16.2812 2.15625 15.875C1.9375 15.5 1.9375 15.0312 2.15625 14.625L8.90625 3.125C9.125 2.75 9.53125 2.5 10 2.5ZM10 6.5C9.5625 6.5 9.25 6.84375 9.25 7.25V10.75C9.25 11.1875 9.5625 11.5 10 11.5C10.4062 11.5 10.75 11.1875 10.75 10.75V7.25C10.75 6.84375 10.4062 6.5 10 6.5ZM11 13.5C11 12.9688 10.5312 12.5 10 12.5C9.4375 12.5 9 12.9688 9 13.5C9 14.0625 9.4375 14.5 10 14.5C10.5312 14.5 11 14.0625 11 13.5Z" fill="#DD4267"/></svg>`

        case 'warning':
          return `<svg width="60" height="60" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10 1.5C5.5625 1.5 2 5.09375 2 9.5C2 13.9375 5.5625 17.5 10 17.5C14.4062 17.5 18 13.9375 18 9.5C18 5.09375 14.4062 1.5 10 1.5ZM9.25 6.25C9.25 5.84375 9.5625 5.5 10 5.5C10.4062 5.5 10.75 5.84375 10.75 6.25V10.25C10.75 10.6875 10.4062 11 10 11C9.5625 11 9.25 10.6875 9.25 10.25V6.25ZM10 14C9.4375 14 9 13.5625 9 13.0312C9 12.5 9.4375 12.0625 10 12.0625C10.5312 12.0625 10.9688 12.5 10.9688 13.0312C10.9688 13.5625 10.5312 14 10 14Z" fill="#11141E"/></svg>`

        case 'info':
        default:
          return `<svg width="60" height="60" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10 17.5C14.4062 17.5 18 13.9375 18 9.5C18 5.09375 14.4062 1.5 10 1.5C5.5625 1.5 2 5.09375 2 9.5C2 13.9375 5.5625 17.5 10 17.5ZM8.75 12H9.5V10H8.75C8.3125 10 8 9.6875 8 9.25C8 8.84375 8.3125 8.5 8.75 8.5H10.25C10.6562 8.5 11 8.84375 11 9.25V12H11.25C11.6562 12 12 12.3438 12 12.75C12 13.1875 11.6562 13.5 11.25 13.5H8.75C8.3125 13.5 8 13.1875 8 12.75C8 12.3438 8.3125 12 8.75 12ZM10 7.5C9.4375 7.5 9 7.0625 9 6.5C9 5.96875 9.4375 5.5 10 5.5C10.5312 5.5 11 5.96875 11 6.5C11 7.0625 10.5312 7.5 10 7.5Z" fill="#868E96"/></svg>`
      }
    }

    // Show error
    await Swal.fire({
      title: titleText,
      text: errorText,
      iconHtml: renderIcon(detail?.client?.type),
      confirmButtonColor: theme.colors.brand[500],
      confirmButtonText: 'Close',
      customClass: {
        // NOTE: check styles on index.html
      }
    })

    // Log analytics
    sdk?.analytics.error(errorText)
  }

  // Show detailed information about the error. This only gets called for BLOCKv errors that have a code
  static showDetail(error: BlockVError, detail?: WalletError) {
    // Show error
    Swal.fire({
      // icon: 'error',
      title: 'Error details',
      html: `
            <table style='text-align: left; font-size: 12px; '>
                <tr><td style='width: 100px; vertical-align: top; '><b>HTTP code</b></td><td>${
                  error.httpStatus || '(none)'
                }</td></tr>
                <tr><td style='width: 100px; vertical-align: top; '><b>Response code</b></td><td>${
                  error.code || '(none)'
                }</td></tr>
                <tr><td style='width: 100px; vertical-align: top; '><b>Response</b></td><td>${
                  error.serverMessage || error.message || '(none)'
                }</td></tr>
                <tr><td style='width: 100px; vertical-align: top; '><b>Request ID</b></td><td>${
                  error.requestID || '(none)'
                }</td></tr>
                <tr><td style='width: 100px; vertical-align: top; '><b>Message</b></td><td>${
                  error.message || '(none)'
                }</td></tr>
            </table>
        `
    })
  }

  static close() {
    return Swal.close()
  }

  /** Do prompt */
  static prompt(options: AlertOptions): Promise<string> {
    // Create promise
    return new Promise((resolve, reject) => {
      // Show alert
      let type: AlertType = 'plain-text'
      if (options.type == 'secure-text') type = 'secure-text'
      ReactAlert.prompt(options.title || '', options.text, resolve, type, '', 'default')
    })
  }
}

export default Alert
